import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { memo, useMemo, useState, useContext, MouseEvent } from 'react';

import WithMenu from '../../../../../../hocs/with-menu';
import { useMenu } from '../../../../../../hooks/useMenu';
import { useMobile } from '../../../../../../hooks/useMobile';
import { useDialog } from '../../../../../../common/dialog/useDialog';

import UserAvatar from './user-avatar';
import { USER_OPTIONS } from './interfaces';
import { getUserActions } from './user-actions';
import { convertUserAccountToInitials } from './utils';
import DialogContentHandler from './dialogContentHandler';
import { ClientStatusContext } from '../../../../../../common/client-status';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}));

const User = () => {
  const classes = useStyles();
  const { isMobile } = useMobile();
  const { menuAnchorEl, handleMenuOpen, handleMenuClose } = useMenu();
  const [optionSelected, setOptionSelected] = useState<USER_OPTIONS | null>(null);
  const { checkIfUserHasPortalPrivilege, userAccount } = useContext(ClientStatusContext);
  const isLoading = !userAccount;

  const {
    isDialogOpen,
    isDialogLocked,
    handleOpenDialog,
    handleCloseDialog,
    handleLockDialog,
    handleUnlockDialog
  } = useDialog();

  const userActions = useMemo(
    () => getUserActions({ isMobile, setOptionSelected, handleOpenDialog, checkIfUserHasPortalPrivilege }),
    [checkIfUserHasPortalPrivilege, handleOpenDialog, isMobile]
  );

  const onCloseHandler = () => {
    handleCloseDialog();
    setOptionSelected(null);
  };

  const onUserAvatarClick = (event: MouseEvent) => {
    if (isLoading) return;

    handleMenuOpen(event);
  };

  return (
    <Box className={classes.container}>
      <WithMenu
        handleClose={handleMenuClose}
        anchorEl={menuAnchorEl}
        items={userActions}
        offset={{ top: 8, right: 16 }}
      >
        <Box onClick={event => onUserAvatarClick(event)}>
          <UserAvatar
            isMobile={isMobile}
            isLoading={isLoading}
            userInitials={convertUserAccountToInitials(userAccount)}
          />
        </Box>
      </WithMenu>
      {optionSelected && (
        <DialogContentHandler
          locked={isDialogLocked}
          open={isDialogOpen}
          handleClose={onCloseHandler}
          handleLock={handleLockDialog}
          handleUnlock={handleUnlockDialog}
          option={optionSelected}
        />
      )}
    </Box>
  );
};

export default memo(User);
